import posthog from 'posthog-js'
import { setErrorReporter, toEventProps, type ErrorReporter } from '../error-reporter'

/**
 * Browser transport. Imported by exactly ONE file:
 * `src/instrumentation-client.ts`, immediately after `posthog.init()`.
 *
 * Don't import this from a component. The port (`../error-reporter`) is the
 * app-facing surface and it is deliberately SDK-free; importing this instead
 * would put `posthog-js` in the graph of whatever imported it, and would break
 * the moment the same component rendered on the server.
 */
const clientReporter: ErrorReporter = {
  capture(report) {
    // `captureException` on an uninitialised posthog-js instance can throw.
    // Not guarded here on purpose — the port's dispatcher owns that try/catch,
    // so there is exactly one place to look for it and adapters stay thin.
    posthog.captureException(report.error, toEventProps(report))
  },

  async flush() {
    // posthog-js batches in a live page; there is no serverless freeze to race
    // and nothing to await. Present so both adapters satisfy one interface.
  },

  /**
   * The consent chokepoint's browser end (`docs/DECISIONS.md` #12).
   *
   * Deliberately broader than error reporting: `opt_out_capturing()` gates every
   * posthog-js call in the process, including the product-analytics
   * `capture()`/`identify()` calls that still import the SDK directly. That
   * makes the banner a one-line change instead of an eleven-file one.
   *
   * ⚠️ Do NOT also set `opt_out_capturing_by_default` in init. That would change
   * behaviour today, and this change is explicitly behaviour-neutral.
   */
  setEnabled(enabled) {
    if (enabled) posthog.opt_in_capturing()
    else posthog.opt_out_capturing()
  },
}

export function installClientErrorReporter(): void {
  setErrorReporter(clientReporter)
}
